<template>
<div>
  <h1 class="pb-5 has-text-weight-bold is-size-3">Scoreboard</h1>

  <div class="box has-text-centered is-size-4" v-if="!loading && !data.length">
    <b-icon icon="frown" class="mr-2" />
    No Data
  </div>
  <div v-else>

    <nav class="level box">
      <div class="level-item has-text-centered">
        <div>
          <p class="heading">Players</p>
          <p class="title">{{ data.length }}</p>
        </div>
      </div>
      <div class="level-item has-text-centered">
        <div>
          <p class="heading">Challenges</p>
          <p class="title">{{ headers.length }}</p>
        </div>
      </div>
      <div class="level-item has-text-centered">
        <div>
          <p class="heading">Unsolved</p>
          <p class="title">{{ unsolvedCount }}</p>
        </div>
      </div>
    </nav>

    <div class="columns">
      <b-loading v-model="loading" />
      <div class="column">
        <div class="scoreboard-chart box" ref="evolutionchart"></div>
      </div>
      <div class="column">
        <div class="scoreboard-chart box" ref="chartdiv"></div>
      </div>
    </div>

    <b-table
      class="scoreboard box p-0"
      :data="data"
      hoverable
      :loading="loading"
      default-sort="position">

      <b-table-column
        field="position"
        label="#"
        header-class="valign-custom"
        numeric centered width='40'
        v-slot="props">
        {{ props.row.position }}
      </b-table-column>

      <b-table-column
        v-if="hasAffiliation"
        field="affiliation"
        label="Course"
        header-class="valign-custom"
        width="80"
        v-slot="props">
        <b-icon icon="minus" v-if="!props.row.affiliation" />
        {{ props.row.affiliation }}
      </b-table-column>

      <b-table-column
        field="nickname"
        label="User"
        header-class="valign-custom"
        v-slot="props">
        <b-icon icon="minus" v-if="!props.row.nickname" />
        {{ props.row.nickname }}
      </b-table-column>

      <b-table-column
        v-for="column in headers"
        :key="column.name"
        field="points"
        :label="column.name"
        numeric centered>
        <template v-slot:default="props">
          <b-icon icon="times" v-if="props.row.challenges[column.name]['points']==0" />
          <span :title="formatDate(props.row.challenges[column.name]['timestamp'])" v-else>
            {{ props.row.challenges[column.name]['points'] }}
          </span>
        </template>
        <template v-slot:header>
          <b-button tag="router-link" :to="'/challenges/' + column.name" type="is-normal" class="scoreboard-button">
            {{ column.name }}
          </b-button>
        </template>
      </b-table-column>

      <b-table-column
        field="total_points"
        label="Leetscore"
        numeric centered width='40'
        header-class="pl-6 pr-5 valign-custom"
        v-slot="props">
        {{ props.row.total_points }}
      </b-table-column>
    </b-table>
  </div>
</div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themesLight from '@amcharts/amcharts4/themes/amcharts';
import am4themesDark from '@amcharts/amcharts4/themes/amchartsdark';

const UPDATE_INTERVAL = 30000;
const TOP_PLAYERS_COUNT = 10;
const MAX_EVOLUTION_EVENTS = 50;

export default {
  data() {
    return {
      loading: true,
      data: [],
      headers: [],
      interval: null,
      solversChart: null,
      evolutionChart: null,
      chartSeries: {},
      evolutionSeries: {},
    };
  },
  computed: {
    unsolvedCount() {
      if (!this.headers.length || !this.data.length) {
        return 0;
      }

      // Count challenges that have zero solvers
      return this.headers.filter((challenge) => {
        const hasSolver = this.data.some((user) => user.challenges[challenge.name] && user.challenges[challenge.name].points > 0);
        return !hasSolver;
      }).length;
    },
    hasAffiliation() {
      return this.data.some((user) => user.affiliation != null && user.affiliation !== '');
    },
  },
  methods: {
    async update() {
      try {
        this.headers = (await this.$ctforge.api.get('/challenges/list')).filter((h) => h.active !== false);
        this.data = await this.$ctforge.api.get('/challenges/scoreboard');
        this.loading = false;
        this.updateCharts();
      } catch (e) {
        this.notify(e.message, 'is-danger');
      }
    },

    updateCharts() {
      this.updateSolversChart();
      this.updateEvolutionChart();
    },

    formatDate(timestamp) {
      return timestamp ? new Date(timestamp).toLocaleString() : '';
    },

    getAllEvents() {
      return this.data.flatMap((user) => Object.entries(user.challenges)
        .filter(([, value]) => value.timestamp !== null)
        .map(([chal, value]) => ({
          name: chal,
          date: new Date(value.timestamp),
          points: value.points,
          user: user.nickname,
        }))).sort((a, b) => a.date - b.date);
    },

    updateSolversChart() {
      const chalNames = this.headers.map((h) => h.name);
      const allEvents = this.getAllEvents();
      const now = new Date();

      if (allEvents.length > 0) {
        const startDate = new Date(allEvents[0].date - 1000);
        chalNames.forEach((name) => {
          allEvents.push({ name, date: startDate, points: 0 });
        });
        allEvents.sort((a, b) => a.date - b.date);
      }

      // Clear existing series and recreate them
      this.solversChart.series.clear();
      this.chartSeries = {};
      this.solversChart.colors.reset();
      let maxSolvers = 0;

      chalNames.forEach((name) => {
        const seriesData = this.calculateSolversData(allEvents, name, now);
        seriesData.forEach((point) => {
          if (point.solvers > maxSolvers) {
            maxSolvers = point.solvers;
          }
        });
        const series = this.getOrCreateSolversSeries(name);
        series.data = seriesData;
      });
      const valueAxis = this.solversChart.yAxes.getIndex(0);
      if (valueAxis) {
        valueAxis.max = Math.ceil(maxSolvers / 50) * 50;
        valueAxis.strictMinMax = true;
      }
    },

    calculateSolversData(allEvents, challengeName, now) {
      let solverCount = -1; // Start from -1 to count from 0
      const seriesData = allEvents
        .filter((v) => v.name === challengeName)
        .map((v) => {
          solverCount += 1;
          return {
            date: v.date,
            solvers: solverCount,
          };
        });

      const lastCount = seriesData.length > 0 ? seriesData[seriesData.length - 1].solvers : 0;
      seriesData.push({ date: now, solvers: lastCount });

      return seriesData;
    },

    getOrCreateSolversSeries(name) {
      if (this.chartSeries[name]) {
        return this.chartSeries[name];
      }

      const series = this.solversChart.series.push(new am4charts.LineSeries());
      series.dataFields.dateX = 'date';
      series.dataFields.valueY = 'solvers';
      series.legendSettings.labelText = name;
      series.strokeWidth = 2;
      series.tooltipText = `${name}: {valueY.value}`;

      this.solversChart.scrollbarX.series.push(series);
      this.chartSeries[name] = series;

      return series;
    },

    createSolversChart() {
      const chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart);
      chart.padding(40, 40, 40, 40);
      chart.colors.minColors = 50;
      chart.colors.step = 2;
      chart.colors.stepOptions = { hue: 0.08 };
      chart.colors.shuffle = true;

      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      dateAxis.renderer.grid.template.location = 0;

      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.tooltip.disabled = true;
      valueAxis.renderer.minWidth = 35;
      valueAxis.title.text = 'Solvers';
      valueAxis.maxPrecision = 0;

      chart.scrollbarX = new am4charts.XYChartScrollbar();
      chart.cursor = new am4charts.XYCursor();
      chart.legend = new am4charts.Legend();
      chart.legend.fontSize = 10;
      chart.legend.labels.template.fontSize = 10;
      chart.legend.valueLabels.template.fontSize = 10;
      chart.legend.markers.template.width = 5;
      chart.legend.markers.template.height = 5;

      this.solversChart = chart;
    },

    updateEvolutionChart() {
      const topPlayers = this.data.slice(0, TOP_PLAYERS_COUNT);
      if (topPlayers.length === 0) {
        return;
      }

      // extract the events of the top players from the challenges field of each user
      const topPlayersEvents = topPlayers.flatMap((user) => Object.entries(user.challenges)
        .map(([chal, value]) => ({
          name: chal,
          date: new Date(value.timestamp),
          points: value.points,
          user: user.nickname,
          user_position: user.position,
        }))).sort((a, b) => a.date - b.date);

      // pick only the latest `MAX_EVOLUTION_EVENTS` events
      const limitedEvents = topPlayersEvents.slice(-MAX_EVOLUTION_EVENTS);
      if (limitedEvents.length === 0) {
        return;
      }

      // compute the initial scores for the top players up to the last event not occurring in `limitedEvents`:
      // use the current position as index to prevent issues with different top players with the same nickname
      const initialScores = {};
      for (let i = 1; i <= topPlayers.length; i += 1) {
        initialScores[i] = 0;
      }
      for (let i = 0; i < topPlayersEvents.length - MAX_EVOLUTION_EVENTS; i += 1) {
        const evt = topPlayersEvents[i];
        initialScores[evt.user_position] += evt.points;
      }

      // calculate cumulative scores for each player over recent events
      const playerEvolution = {};
      const currentScores = { ...initialScores };

      limitedEvents.forEach((event, index) => {
        const nextScore = currentScores[event.user_position] + event.points;
        currentScores[event.user_position] = nextScore;

        if (!playerEvolution[event.user_position]) {
          playerEvolution[event.user_position] = [];
        }

        playerEvolution[event.user_position].push({
          round: index + 1,
          score: nextScore,
          date: event.date,
        });
      });

      const activeTopPlayers = topPlayers.filter((user) => (
        playerEvolution[user.position] && playerEvolution[user.position].length > 0
      ));

      // clear existing series
      this.evolutionChart.series.clear();

      // update series for top players
      const maxRound = limitedEvents.length;
      activeTopPlayers.forEach((user) => {
        const evolution = playerEvolution[user.position];

        const lastScore = evolution[evolution.length - 1].score;
        const dataWithStartAndEnd = [
          { round: 0, score: initialScores[user.position], date: new Date(evolution[0].date.getTime() - 1000) },
          ...evolution,
          { round: maxRound, score: lastScore, date: new Date(evolution[evolution.length - 1].date.getTime() + 1000) },
        ];

        const series = this.evolutionChart.series.push(new am4charts.LineSeries());
        series.dataFields.valueX = 'round';
        series.dataFields.valueY = 'score';
        series.name = user.nickname;
        series.strokeWidth = 2;
        if (user.position <= this.evolutionChart.colors.list.length) {
          series.stroke = this.evolutionChart.colors.list[user.position - 1];
          series.fill = this.evolutionChart.colors.list[user.position - 1];
        }
        series.tooltipText = `${user.nickname}: [bold]{valueY}[/] points`;

        series.data = dataWithStartAndEnd;
      });
    },

    createEvolutionChart() {
      const chart = am4core.create(this.$refs.evolutionchart, am4charts.XYChart);
      chart.padding(40, 40, 40, 40);

      // TODO improve random color generation instead of hardcoding
      const colors = [
        '#1F77B4', // strong blue
        '#D62728', // strong red
        '#2CA02C', // strong green
        '#FF7F0E', // vivid orange
        '#9467BD', // purple
        '#17BECF', // cyan
        '#8C564B', // dark brown
        '#E377C2', // magenta
        '#7F7F7F', // dark gray
        '#BCBD22', // olive
      ];
      chart.colors.list = colors.map((c) => am4core.color(c));

      // chart.colors.minColors = 50;
      // chart.colors.step = 2;
      // chart.colors.stepOptions = { hue: 0.08 };
      // chart.colors.shuffle = true;

      const valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
      valueAxis.renderer.minGridDistance = 50;
      valueAxis.maxPrecision = 0;
      valueAxis.min = 0;
      valueAxis.strictMinMax = true;
      valueAxis.renderer.labels.template.disabled = true;
      valueAxis.cursorTooltipEnabled = false;

      const scoreAxis = chart.yAxes.push(new am4charts.ValueAxis());
      scoreAxis.title.text = 'Top 10 Scores';
      scoreAxis.renderer.minWidth = 50;
      scoreAxis.maxPrecision = 0;
      scoreAxis.min = 0;

      chart.cursor = new am4charts.XYCursor();
      chart.legend = new am4charts.Legend();
      chart.legend.position = 'bottom';
      chart.legend.fontSize = 10;
      chart.legend.labels.template.fontSize = 10;
      chart.legend.valueLabels.template.fontSize = 10;
      chart.legend.markers.template.width = 10;
      chart.legend.markers.template.height = 10;
      chart.legend.scrollable = true;

      this.evolutionChart = chart;
    },
    applyChartTheme() {
      am4core.useTheme(this.isDarkTheme() ? am4themesDark : am4themesLight);
    },
    isDarkTheme() {
      return getComputedStyle(document.body).getPropertyValue('--is-dark-theme') === 'true';
    },
  },

  async mounted() {
    document.title = 'Scoreboard';
    this.applyChartTheme();
    this.createSolversChart();
    this.createEvolutionChart();

    await this.update();
    this.interval = setInterval(() => this.update(), UPDATE_INTERVAL);
  },

  beforeDestroy() {
    if (this.solversChart) this.solversChart.dispose();
    if (this.evolutionChart) this.evolutionChart.dispose();
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  },
};
</script>

<style>
th {
  vertical-align: middle !important;
}

.scoreboard-chart {
  width: 100%;
  height: 400px;
  padding: 0 !important;
}

.scoreboard-button {
    max-width: 8em;
    padding-left: 0.3em !important;
    padding-right: 0.3em !important;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block !important;

    border: 0 !important;
    transform: rotate(-45deg);
    transform-origin: 16% 100%;
    display: inline-block;
    position: absolute !important;
}

.scoreboard thead {
  height: 8em;
}
</style>
